import { Edge, Node } from 'reactflow'

import { SetEdgesType, SetNodesType } from '@ui/stores/pipelinesWorkflow/NodeAndEdgeStore'

import { createNewEdges } from './createNewEdges'
import { getClosestNode } from './getClosestNode'
import { getParent } from './getParent'
import { getRelationType } from './getRelationType'

//
// Sudo code:
// 1. Un-attached nodes
//   - can connect
//     a. Up and down of another node: become a sibling
//     b. Left of another node: become the node's parent
//     c. Right of another node: become the node's child
//     d. Right of the firehose
//   - can not connect
//     a. Up, down, or left of the firehose
//     b. Right of a placeholder
// 2. Attached nodes
//   - can not
//     a. change parents
//
export const onNodeDrag = (
  targetNode: Node,
  nodes: Node[],
  edges: Edge[],
  setNodes: (nodes: SetNodesType) => void,
  setEdges: (edges: SetEdgesType) => void
) => {
  const firehoseNode = nodes.find((node) => node.type === 'firehose')

  if (!firehoseNode) {
    return
  }

  // Don't let an already attached node be dragged:
  // If there's an edge that connects to the dragged node, and it's not a drag edge
  // then don't allow the drag.
  if (edges.find((edge) => edge.target === targetNode.id && edge.type !== 'drag')) {
    return
  }
  // If the node is the firehose, don't allow the drag
  if (targetNode.type === 'firehose') {
    return
  }

  // If there's no close node, force a connection to the firehose.
  // We don't want unattached root nodes floating around.
  //
  // If we force the connection to the firehose, relationType will be null since it's not close.
  const closeNode: Node = getClosestNode(targetNode, nodes) || firehoseNode
  const relationType = getRelationType(targetNode, closeNode)

  // Get the parent of the node we are closest to, to be either a sibling (top / bottom) of become the new parent (left)
  // In the case of the firehose, this will return null
  const closeNodeParent = getParent(closeNode, nodes, edges)
  const newEdgesByProximity = createNewEdges(targetNode, closeNode, relationType, closeNodeParent) || []

  // Make the node appear highlighted if it's near a node and forming a new connection
  const highlightNode = newEdgesByProximity.length > 0

  setNodes((nodes: Node[]) =>
    // we can't manipulate the node directly, so we create a new array of nodes
    nodes.map((node) => {
      if (node.id === targetNode.id) {
        return {
          ...node,
          data: {
            ...node.data,
            ogtype: node.type === 'drag' ? 'code' : node.type,
            highlight: highlightNode,
          },
          type: 'drag',
        }
      }

      return node
    })
  )

  setEdges((edges: Edge[]) => {
    // Find all the edges that are not the 'dashed' drag edge
    const nextEdges = edges.filter((edge) => edge.type !== 'drag').concat(newEdgesByProximity)

    if (newEdgesByProximity.length === 0) {
      nextEdges.push({
        id: `firehose=>${targetNode.id}`,
        source: 'firehose',
        target: targetNode.id,
        type: 'drag',
      })
    }

    const hideOldEdges = nextEdges.map((edge: Edge) => {
      // If we are being added as a child or a sibling, and there was a previously hidden edge
      // (meaning a node was dragged betwee two nodes)
      // then unhide the edge
      if (relationType !== 'left' && edge.className === 'hidden-during-drag') {
        edge.className = ''
      }

      // If we are going between two nodes, and this edge currently connects the parent and the closest node
      // hide the edge so we can show our new node and two edges
      if (
        relationType === 'left' &&
        closeNodeParent &&
        edge.source === closeNodeParent.id &&
        edge.target === closeNode.id
      ) {
        edge.className = 'hidden-during-drag'
      }

      return edge
    })

    return hideOldEdges
  })
}
